import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as UUID } from 'uuid';
import { SubmitHandler, useForm } from 'react-hook-form';
import classNames from 'classnames';
import * as styles from './KeywordFormSection.module.scss';
import { validateKeyword } from './validateKeyword';
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Icon,
  IconList,
  List,
  ListItem,
  Text,
  VisuallyHidden,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { HighlightsResult, HighlightType } from 'query/review/Highlights';
import { useHighlights } from 'hooks/reviews/useHighlights';

type RemovedKeyword = { keyword: string; index: number; isDeleting: boolean };

type FormValues = {
  newKeyword: string;
};

type KeywordFormSectionProps = {
  title: string;
  titleColor: 'success' | 'danger';
  label: string;
  type: HighlightType;
  onHighlightChangeError: () => void;
  onHighlightChangeSuccess?: (highlights: HighlightsResult) => void;
};

type KeywordListProps = {
  title: string;
  keywords: string[];
  pendingKeywords: string[];
  onDelete: (keyword: string) => void;
  onLastKeywordRemoved: () => void;
};

const KeywordList = ({
  title,
  keywords,
  pendingKeywords,
  onDelete,
  onLastKeywordRemoved,
}: KeywordListProps): JSX.Element => {
  const [removed, setRemoved] = useState<RemovedKeyword | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const remove = (keyword: string) => {
    setRemoved({
      keyword,
      index: keywords.indexOf(keyword),
      isDeleting: false,
    });
    onDelete(keyword);
  };

  // The focused delete button leaves the DOM with its word, so focus moves to the one now
  // in its place, or back to the form when the list is empty. A word still listed after its
  // delete settled was a failed delete: being disabled while pending dropped focus, so its
  // button is focused again and forgotten.
  useEffect(() => {
    if (!removed) return;
    const deleteButtons = listRef.current?.querySelectorAll('button') ?? [];
    if (keywords.includes(removed.keyword)) {
      const isPending = pendingKeywords.includes(removed.keyword);
      if (isPending && !removed.isDeleting)
        setRemoved({ ...removed, isDeleting: true });
      if (!isPending && removed.isDeleting) {
        setRemoved(null);
        deleteButtons[keywords.indexOf(removed.keyword)]?.focus();
      }
      return;
    }
    setRemoved(null);
    if (keywords.length === 0) {
      onLastKeywordRemoved();
      return;
    }
    deleteButtons[Math.min(removed.index, keywords.length - 1)]?.focus();
  }, [keywords, pendingKeywords, removed, onLastKeywordRemoved]);

  return (
    <div ref={listRef}>
      <Stack spacing={2} component={List} className={styles.highlightList}>
        {keywords.map((keyword) => (
          <Inline alignX="left" key={keyword} spacing={3} component={ListItem}>
            <Text size="sm" className={styles.highlight} variant="neutral">
              {keyword}
            </Text>
            <Button
              iconOnly
              htmlType="button"
              type="neutral"
              variant="ghost"
              size="sm"
              isLoading={pendingKeywords.includes(keyword)}
              onClick={() => remove(keyword)}
              aria-label={`Delete ${keyword} from ${title.toLowerCase()} words`}
            >
              <Icon size="md" icon={IconList.light.faTrashCan} />
            </Button>
          </Inline>
        ))}
      </Stack>
    </div>
  );
};

const KeywordFormSection = ({
  title,
  titleColor,
  label,
  type,
  onHighlightChangeError,
  onHighlightChangeSuccess,
}: KeywordFormSectionProps): JSX.Element => {
  const describedById = useMemo(() => UUID(), []);
  // Queued deletes must each keep spinning until their own request returns.
  const [pendingKeywords, setPendingKeywords] = useState<string[]>([]);
  const [announcement, setAnnouncement] = useState('');
  const {
    highlights,
    isAddingHighlight,
    addHighlight,
    removeHighlight,
  } = useHighlights();
  const keywords = highlights?.[`${type}Keywords`] ?? [];

  const {
    register,
    resetField,
    setFocus,
    handleSubmit,
    formState,
  } = useForm<FormValues>({ defaultValues: { newKeyword: '' } });

  const newKeywordError = formState.errors.newKeyword?.message;

  const handleAddKeyword: SubmitHandler<FormValues> = (data, event) => {
    event?.preventDefault();

    const newWord = data.newKeyword.trim();
    if (!newWord) return;

    // The Add button is disabled while saving, which drops focus to the body; hand it to the input afterwards.
    const submittedFromButton =
      document.activeElement instanceof HTMLButtonElement;
    const focusWasDropped = () => document.activeElement === document.body;

    setAnnouncement('');
    addHighlight(
      { type, keyword: newWord },
      {
        onSuccess: (saved) => {
          onHighlightChangeSuccess?.(saved);
          resetField('newKeyword');
          setAnnouncement(`Added ${newWord}`);
        },
        onError: onHighlightChangeError,
        onSettled: () => {
          if (submittedFromButton && focusWasDropped()) setFocus('newKeyword');
        },
      }
    );
  };

  const handleDeleteKeyword = (keyword: string) => {
    setAnnouncement('');
    setPendingKeywords((pending) => [...pending, keyword]);
    removeHighlight(
      { type, keyword },
      {
        onSuccess: (saved) => {
          onHighlightChangeSuccess?.(saved);
          setAnnouncement(`Removed ${keyword}`);
        },
        onError: onHighlightChangeError,
        onSettled: () =>
          setPendingKeywords((pending) =>
            pending.filter((word) => word !== keyword)
          ),
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(handleAddKeyword)} className={styles.form}>
      <Stack spacing={3} className={styles.formGroup} component="fieldset">
        <Text
          className={styles.formLegend}
          variant={titleColor}
          component="legend"
          size="sm"
        >
          {title}
        </Text>
        <Inline alignY="top" spacing={3} shouldWrap={false}>
          <FormControl error={newKeywordError} className={styles.keywordInput}>
            <FormLabel isVisuallyHidden>{label}</FormLabel>
            <Input
              size="sm"
              aria-describedby={describedById}
              {...register('newKeyword', {
                validate: (value) => validateKeyword(value, keywords),
              })}
            />
            <Text
              size="xs"
              variant="weak"
              id={describedById}
              className={classNames({
                [styles.visuallyHidden]: Boolean(newKeywordError),
              })}
            >
              Add a word or phrase
            </Text>
          </FormControl>

          <Button
            isLoading={isAddingHighlight}
            htmlType="submit"
            type="brand"
            variant="outline"
            aria-label="Add"
            size="sm"
          >
            Add
          </Button>
        </Inline>

        <VisuallyHidden component="div" aria-live="polite" role="status">
          {announcement}
        </VisuallyHidden>

        <KeywordList
          title={title}
          keywords={keywords}
          pendingKeywords={pendingKeywords}
          onDelete={handleDeleteKeyword}
          onLastKeywordRemoved={() => setFocus('newKeyword')}
        />
      </Stack>
    </form>
  );
};

export default KeywordFormSection;
